
import React, { useState, useEffect } from 'react';
import { X, Trash2, Save, MoreHorizontal, Sparkles, Plus, Edit2, Eye } from 'lucide-react';
import { DataModelMeasureWizard } from './DataModelMeasureWizard';
import { DataPreviewModal } from '@/components/common/DataPreviewModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface InspectorProps {
    selectedElement: any;
    // Model Level Props
    modelName: string;
    onUpdateModelName: (name: string) => void;
    measures: any[];
    onUpdateMeasures: (measures: any[]) => void;
    // Storage Configuration
    storageMode?: string;
    onUpdateStorageMode?: (mode: string) => void;
    dataLayer?: string;
    onUpdateDataLayer?: (layer: string) => void;
    refreshSchedule?: string;
    onUpdateRefreshSchedule?: (schedule: string) => void;
    // Context for Wizard
    nodes?: any[];
    fromFlow?: boolean;  // Hide storage config when true
    // Element Level Props
    onUpdate: (id: string, data: any) => void;
    onDelete: (id: string) => void;
    onClose: () => void;
    dataSourceId?: string;
    tableStats?: any;
}

const DataModelInspector: React.FC<InspectorProps> = ({
    selectedElement,
    modelName,
    onUpdateModelName,
    measures,
    onUpdateMeasures,
    onUpdate,
    onDelete,
    onClose,
    nodes = [],
    storageMode = "IMPORT",
    onUpdateStorageMode,
    dataLayer = "BRONZE",
    onUpdateDataLayer,
    refreshSchedule = "MANUAL",
    onUpdateRefreshSchedule,
    fromFlow = false,
    dataSourceId,
    tableStats
}) => {
    const [localData, setLocalData] = useState<any>(selectedElement?.data || null);

    // New Measure State
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [editingMeasure, setEditingMeasure] = useState<any>(null);

    // Preview State
    const [previewOpen, setPreviewOpen] = useState(false);

    const formatDuration = (seconds?: number | null) => {
        if (seconds === null || seconds === undefined) return '';
        if (seconds < 60) return `${Math.round(seconds)}s`;

        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.round(seconds % 60);

        const parts = [];
        if (h > 0) parts.push(`${h}h`);
        if (m > 0) parts.push(`${m}m`);
        if (s > 0 || (h === 0 && m === 0)) parts.push(`${s}s`);

        return parts.join(' ');
    };

    const handleOpenWizard = (measure?: any) => {
        setEditingMeasure(measure || null);
        setIsWizardOpen(true);
    };

    const handleSaveMeasure = (measure: any) => {
        if (editingMeasure) {
            // Update
            onUpdateMeasures(measures.map(m => m.id === measure.id ? measure : m));
        } else {
            // Create
            onUpdateMeasures([...measures, { ...measure, id: `measure_${Date.now()}` }]);
        }
        setIsWizardOpen(false);
    };

    const handleDeleteMeasure = (id: string) => {
        onUpdateMeasures(measures.filter(m => m.id !== id));
    };

    // Derive tables for wizard
    const availableTables = nodes.map(n => ({
        name: n.data.source_table || n.data.label,
        alias: n.data.label
    }));

    useEffect(() => {
        if (selectedElement) {
            setLocalData({ ...selectedElement.data });
        } else {
            setLocalData(null);
        }
    }, [selectedElement]);

    const handleChange = (key: string, value: any) => {
        const updated = { ...localData, [key]: value };
        setLocalData(updated);
        onUpdate(selectedElement.id, updated);
    };

    if (!selectedElement) {
        return (
            <div className="w-80 border-l border-gray-200 bg-white flex flex-col h-full shadow-lg z-10">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <span className="font-semibold text-sm">Model Properties</span>
                </div>
                <div className="p-4 space-y-6 flex-1 overflow-y-auto">
                    <div className="space-y-2">
                        <Label>Model Name</Label>
                        <Input value={modelName} onChange={(e) => onUpdateModelName(e.target.value)} />
                    </div>

                    {/* Storage Configuration - Hidden when invoked from Flow */}
                    {!fromFlow && (
                        <div className="space-y-4 border-t border-gray-200 pt-4">
                            <Label className="text-sm font-semibold">Storage Configuration</Label>

                            <div className="space-y-2">
                                <Label className="text-xs">Data Layer (Medallion)</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {['BRONZE', 'SILVER', 'GOLD'].map(layer => (
                                        <button
                                            key={layer}
                                            className={`px-3 py-2 text-xs font-medium rounded border transition-colors ${dataLayer === layer
                                                ? 'bg-amber-50 border-amber-300 text-amber-700'
                                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                                }`}
                                            onClick={() => onUpdateDataLayer?.(layer)}
                                        >
                                            {layer}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-gray-400">Raw ingestion → Cleansed → Curated</p>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs">Storage Mode</Label>
                                <Select value={storageMode} onValueChange={(val) => onUpdateStorageMode?.(val)}>
                                    <SelectTrigger className="h-12 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                        <SelectItem value="DIRECT_QUERY">
                                            <div className="text-left py-1">
                                                <div className="font-semibold">DirectQuery</div>
                                                <div className="text-[10px] text-gray-500">Live connection</div>
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="IMPORT">
                                            <div className="text-left py-1">
                                                <div className="font-semibold">Import</div>
                                                <div className="text-[10px] text-gray-500">Cache data</div>
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs">Refresh Schedule</Label>
                                <Select value={refreshSchedule} onValueChange={(val) => onUpdateRefreshSchedule?.(val)}>
                                    <SelectTrigger className="h-9 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                        <SelectItem value="MANUAL">Manual Refresh Only</SelectItem>
                                        <SelectItem value="HOURLY">Every Hour</SelectItem>
                                        <SelectItem value="DAILY">Daily</SelectItem>
                                        <SelectItem value="WEEKLY">Weekly</SelectItem>
                                        <SelectItem value="MONTHLY">Monthly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <Label>Measures</Label>
                            <Button size="sm" variant="outline" onClick={() => handleOpenWizard()}>
                                <Plus className="w-3 h-3 mr-1" /> Add
                            </Button>
                        </div>

                        <div className="space-y-2">
                            {measures.map(m => (
                                <div key={m.id} className="p-2 border border-gray-100 rounded-md flex justify-between items-start group hover:bg-gray-50">
                                    <div className="cursor-pointer flex-1" onClick={() => handleOpenWizard(m)}>
                                        <div className="font-medium text-xs flex items-center gap-2">
                                            {m.name}
                                            <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                                        </div>
                                        <div className="text-[10px] text-gray-500 font-mono truncate max-w-[180px]">{m.expression}</div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-red-500 hover:bg-red-50"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteMeasure(m.id); }}
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                            {measures.length === 0 && (
                                <div className="text-xs text-gray-400 text-center py-4">No measures defined</div>
                            )}
                        </div>
                    </div>
                </div>

                <DataModelMeasureWizard
                    isOpen={isWizardOpen}
                    onClose={() => setIsWizardOpen(false)}
                    onSave={handleSaveMeasure}
                    onDelete={handleDeleteMeasure}
                    initialData={editingMeasure}
                    availableTables={availableTables}
                    existingMeasures={measures}
                />
            </div>
        );
    }

    const isNode = !selectedElement.source; // Edges have 'source' prop
    const isTable = isNode && localData?.type === 'PHYSICAL';

    if (!localData) return null;

    return (
        <div className="w-80 border-l border-gray-200 bg-white flex flex-col h-full shadow-lg z-10">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <span className="font-semibold text-sm">
                    {isNode ? 'Table Properties' : 'Relationship Properties'}
                </span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
                    <X className="w-4 h-4" />
                </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isNode ? (
                    <>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Table Alias</Label>
                                <span className="text-[10px] text-purple-600 font-medium">✏️ Editable</span>
                            </div>
                            <Input
                                value={localData.label || ''}
                                onChange={(e) => handleChange('label', e.target.value)}
                                placeholder="Enter a friendly name"
                                className="border-purple-200 focus:border-purple-400"
                            />
                            <p className="text-[10px] text-gray-500">Used in SQL queries and relationships</p>
                        </div>

                        {isTable && tableStats && (
                            <div className="bg-gray-50 dark:bg-slate-800/50 p-3 rounded-lg border border-gray-100 dark:border-slate-800 mb-4 text-xs">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-gray-500 font-medium tracking-wide">METRICS</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${tableStats.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                        {tableStats.status === 'success' ? 'Extracted' : 'Failed'}
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-center text-gray-700 dark:text-slate-300">
                                    <div className="flex flex-col">
                                        <span className="font-bold text-sm">{(tableStats.row_count || 0).toLocaleString()}</span>
                                        <span className="text-[10px] text-gray-500">Rows</span>
                                    </div>
                                    <div className="flex flex-col border-l border-gray-200 dark:border-slate-700">
                                        <span className="font-bold text-sm">{formatDuration(tableStats.duration_seconds || 0)}</span>
                                        <span className="text-[10px] text-gray-500">Duration</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isTable && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label>Source Table</Label>
                                    <span className="text-[10px] text-purple-600 font-medium">✏️ Editable</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Input
                                        value={localData.source_table || ''}
                                        onChange={(e) => handleChange('source_table', e.target.value)}
                                        placeholder="schema.table_name"
                                        className="border-purple-200 focus:border-purple-400 font-mono text-sm flex-1"
                                    />
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="shrink-0 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                        onClick={() => setPreviewOpen(true)}
                                        title="Preview Table Data"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                </div>
                                <p className="text-[10px] text-gray-500">You can change the source table at any time</p>
                            </div>
                        )}

                        {!isTable && (
                            <div className="space-y-2">
                                <Label>SQL Definition</Label>
                                <Textarea
                                    value={localData.sql_definition || ''}
                                    onChange={(e) => handleChange('sql_definition', e.target.value)}
                                    placeholder="SELECT * FROM ..."
                                    className="font-mono text-xs h-32"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Join Type</Label>
                                <span className="text-[10px] text-purple-600 font-medium">✏️ Editable</span>
                            </div>
                            <Select
                                value={localData.joinType || 'INNER'}
                                onValueChange={(val) => handleChange('joinType', val)}
                            >
                                <SelectTrigger className="border-purple-200 focus:border-purple-400">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white z-50 shadow-lg border-gray-200">
                                    <SelectItem value="INNER">Inner Join</SelectItem>
                                    <SelectItem value="LEFT">Left Join</SelectItem>
                                    <SelectItem value="RIGHT">Right Join</SelectItem>
                                    <SelectItem value="FULL">Full Join</SelectItem>
                                    <SelectItem value="CROSS">Cross Join</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Join Condition</Label>
                                <span className="text-[10px] text-purple-600 font-medium">✏️ Editable</span>
                            </div>
                            <Textarea
                                value={localData.joinCondition || ''}
                                onChange={(e) => handleChange('joinCondition', e.target.value)}
                                placeholder="t1.id = t2.customer_id"
                                className="font-mono text-xs h-24 border-purple-200 focus:border-purple-400"
                            />
                            <p className="text-[10px] text-gray-500">
                                Use table aliases defined in your nodes. You can modify this at any time.
                            </p>
                        </div>
                    </>
                )}

                {/* Column Selection & Synonyms */}
                {isTable && selectedElement.selectedColumn && (
                    <div className="mt-6 pt-4 border-t border-gray-100">
                        <div className="bg-purple-50 p-3 rounded-md mb-4 border border-purple-100">
                            <div className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-1">Selected Column</div>
                            <div className="text-sm font-bold text-gray-800 break-all">
                                {localData.label || localData.source_table}
                                <span className="text-purple-600">.{selectedElement.selectedColumn.name}</span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* Business Name */}
                            <div className="space-y-1">
                                <Label className="text-xs">Business Name</Label>
                                <Input
                                    className="h-8 text-xs"
                                    placeholder="e.g. Revenue"
                                    value={
                                        localData.columns?.find(c => c.name === selectedElement.selectedColumn.name)?.business_name || ''
                                    }
                                    onChange={(e) => {
                                        const colName = selectedElement.selectedColumn.name;
                                        const val = e.target.value;
                                        const updatedColumns = (localData.columns || []).map(c => {
                                            if (c.name === colName) {
                                                return { ...c, business_name: val };
                                            }
                                            return c;
                                        });
                                        handleChange('columns', updatedColumns);
                                    }}
                                />
                            </div>

                            {/* Description */}
                            <div className="space-y-1">
                                <Label className="text-xs">Description / Meaning</Label>
                                <Textarea
                                    placeholder="Describe what this data represents..."
                                    className="h-16 text-xs resize-none"
                                    value={
                                        localData.columns?.find(c => c.name === selectedElement.selectedColumn.name)?.description || ''
                                    }
                                    onChange={(e) => {
                                        const colName = selectedElement.selectedColumn.name;
                                        const val = e.target.value;
                                        const updatedColumns = (localData.columns || []).map(c => {
                                            if (c.name === colName) {
                                                return { ...c, description: val };
                                            }
                                            return c;
                                        });
                                        handleChange('columns', updatedColumns);
                                    }}
                                />
                            </div>

                            {/* Synonyms */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                                    <Label className="text-xs font-semibold text-purple-700">AI Synonyms / Keywords</Label>
                                </div>
                                <Textarea
                                    placeholder="comma, separated, keywords"
                                    className="h-20 text-xs"
                                    value={
                                        localData.columns?.find(c => c.name === selectedElement.selectedColumn.name)?.synonyms || ''
                                    }
                                    onChange={(e) => {
                                        const colName = selectedElement.selectedColumn.name;
                                        const val = e.target.value;
                                        const updatedColumns = (localData.columns || []).map(c => {
                                            if (c.name === colName) {
                                                return { ...c, synonyms: val };
                                            }
                                            return c;
                                        });
                                        handleChange('columns', updatedColumns);
                                    }}
                                />
                                <p className="text-[10px] text-gray-500 italic">
                                    e.g. "Income", "Sales", "Total Amount"
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
                <Button
                    variant="destructive"
                    className="w-full"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(selectedElement.id);
                    }}
                >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete {isNode ? 'Table' : 'Relationship'}
                </Button>
            </div>

            {/* DATA PREVIEW MODAL */}
            {isTable && localData?.source_table && dataSourceId && (
                <DataPreviewModal
                    isOpen={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    dataSourceId={dataSourceId}
                    objectName={localData.source_table}
                />
            )}
        </div>
    );
};

export default DataModelInspector;
